import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { Notes as AppNotes } from '@unite-us/app-components';
import useDeleteNote from 'src/actions/Case/Contact/Group/useDeleteNote';
import { useInvalidateQueries } from 'src/api/APIHooks';
import { isHttpSuccess } from 'src/api/utils/httpStatus';
import Notifier from 'common/helpers/Notifier';
import DialogV2 from 'common/modal/DialogV2';
import {
  hasDeleteNoteIcon,
  hasEditableNotesRole,
  hbh1556RideHealthLinkMapping,
} from 'common/utils/FeatureFlags/flags';
import {
  canDeleteNote as getCanDeleteNote,
  shouldShowEditButton,
} from './utils/noteUtils';
import usePreparedNotes from './hooks/usePreparedNotes';
import EditNoteForm from './EditNoteForm';
import NoteDeleteContent from './components/NoteDeleteContent';
import { transformNotes } from './transformNotes';

const NotesAdapter = ({
  currentEmployeeId,
  durations,
  editable,
  isClientsDrawer,
  onAddNote,
  providerId,
  reason,
  rideHealthLinkMapping,
  showDeleteNoteIcon,
  showEditNoteIcon,
  subject,
  subjectType,
}) => {
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const deleteDialogRef = useRef(null);

  const {
    displayNotes,
    page,
    pageSize,
    paging,
    setPageNumber,
    setPageSize,
    isLoading,
    rideHealthConfig,
  } = usePreparedNotes({
    isClientsDrawer,
    rideHealthLinkMapping,
    providerId,
    subject,
    subjectType,
  });

  const invalidateQueries = useInvalidateQueries();
  const { deleteRecord: deleteNote } = useDeleteNote();

  const rawNoteById = useMemo(() => {
    const map = {};
    displayNotes.forEach((note) => { map[note.id] = note; });
    return map;
  }, [displayNotes]);

  const noteShapes = useMemo(
    () => transformNotes(displayNotes, durations, reason),
    [displayNotes, durations, reason],
  );

  const canDeleteNote = useCallback((noteShape) => {
    const raw = rawNoteById[noteShape.id];
    if (!raw) return false;
    return getCanDeleteNote({
      currentEmployeeId,
      details: raw.details,
      note: raw,
      showDeleteNoteIcon,
    });
  }, [rawNoteById, currentEmployeeId, showDeleteNoteIcon]);

  const canEditNote = useCallback((noteShape) => {
    const raw = rawNoteById[noteShape.id];
    if (!raw) return false;
    return shouldShowEditButton({
      category: raw.category,
      currentEmployeeId,
      note: raw,
      showEditNoteIcon,
    });
  }, [rawNoteById, currentEmployeeId, showEditNoteIcon]);

  const openDeleteDialog = useCallback((noteId) => {
    setDeletingNoteId(noteId);
    if (deleteDialogRef.current) deleteDialogRef.current.openDialog();
  }, []);

  const cancelDelete = useCallback(() => {
    setDeletingNoteId(null);
    if (deleteDialogRef.current) deleteDialogRef.current.closeDialog();
  }, []);

  const confirmDelete = useCallback(async () => {
    cancelDelete();
    const response = await deleteNote(deletingNoteId, {});
    if (isHttpSuccess(response.status)) {
      Notifier.dispatch(response.status, 'Note deleted');
      invalidateQueries('notes');
    }
  }, [cancelDelete, deleteNote, deletingNoteId, invalidateQueries]);

  const editingNote = editingNoteId ? rawNoteById[editingNoteId] : null;

  return (
    <div>
      {editingNote ? (
        <EditNoteForm
          note={editingNote}
          onCancel={() => setEditingNoteId(null)}
        />
      ) : null}
      <AppNotes
        canDeleteNote={canDeleteNote}
        canEditNote={canEditNote}
        compactMode={isClientsDrawer}
        durationOptions={durations}
        isLoading={isLoading}
        notes={noteShapes}
        onAddNote={editable ? onAddNote : null}
        onDeleteNote={openDeleteDialog}
        onEditNote={setEditingNoteId}
        onPageChange={setPageNumber}
        onPageSizeChange={setPageSize}
        page={page}
        pageSize={pageSize}
        renderTextAsHtml={Boolean(rideHealthConfig)}
        totalItemCount={get(paging, 'total_count', 0)}
        totalPageCount={get(paging, 'total_pages', 0)}
      />
      <DialogV2
        ref={deleteDialogRef}
        title="Delete Note"
        confirmLabel="Delete"
        cancelHandler={cancelDelete}
        confirmationHandler={confirmDelete}
      >
        <NoteDeleteContent />
      </DialogV2>
    </div>
  );
};

NotesAdapter.propTypes = {
  currentEmployeeId: PropTypes.string.isRequired,
  durations: PropTypes.array.isRequired,
  editable: PropTypes.bool,
  isClientsDrawer: PropTypes.bool,
  onAddNote: PropTypes.func.isRequired,
  providerId: PropTypes.string.isRequired,
  reason: PropTypes.string,
  rideHealthLinkMapping: PropTypes.object,
  showDeleteNoteIcon: PropTypes.bool.isRequired,
  showEditNoteIcon: PropTypes.bool.isRequired,
  subject: PropTypes.string.isRequired,
  subjectType: PropTypes.string.isRequired,
};

NotesAdapter.defaultProps = {
  editable: true,
  isClientsDrawer: false,
  reason: null,
  rideHealthLinkMapping: {},
};

const mapStateToProps = (state, ownProps) => ({
  currentEmployeeId: get(state, 'globalState.currentEmployee.id', ''),
  durations: get(state, 'session.enums.time.duration', []),
  onAddNote: ownProps.onAddNote,
  providerId: ownProps.providerId,
  rideHealthLinkMapping: hbh1556RideHealthLinkMapping(state),
  showDeleteNoteIcon: hasDeleteNoteIcon(state),
  showEditNoteIcon: hasEditableNotesRole(state),
  subject: ownProps.subject,
  subjectType: ownProps.subjectType,
});

export default connect(mapStateToProps)(NotesAdapter);
